import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/kunlun-data';

const categoryImages: Record<string, string> = {
  'glacier-source': 'https://readdy.ai/api/search-image?query=A%20luxurious%20glass%20bottle%20of%20premium%20mineral%20water%20on%20a%20clean%20white%20minimalist%20surface%20with%20soft%20natural%20lighting%2C%20elegant%20product%20photography%2C%20ice-like%20textures&width=800&height=500&seq=cat-glacier-detail&orientation=landscape',
  'snow-spring': 'https://readdy.ai/api/search-image?query=A%20crystal%20clear%20bottle%20of%20pure%20spring%20water%20on%20smooth%20river%20stones%20with%20soft%20morning%20sunlight%2C%20natural%20organic%20minimalist%20product%20photography&width=800&height=500&seq=cat-spring-detail&orientation=landscape',
  'limited-edition': 'https://readdy.ai/api/search-image?query=A%20handcrafted%20crystal%20glass%20water%20bottle%20with%20gold%20accents%20on%20dark%20marble%20with%20dramatic%20lighting%2C%20exclusive%20limited%20edition%20product%20photography&width=800&height=500&seq=cat-limited-detail&orientation=landscape',
};

const productImages: Record<string, string> = {
  'prod-glacier-classic': 'https://readdy.ai/api/search-image?query=A%20single%20elegant%20premium%20mineral%20water%20bottle%20with%20clean%20modern%20label%20design%20isolated%20on%20pure%20white%20background%2C%20soft%20studio%20lighting&width=400&height=300&seq=prod-glacier-classic&orientation=landscape',
  'prod-glacier-premium': 'https://readdy.ai/api/search-image?query=A%20large%20premium%20glass%20water%20bottle%20with%20sophisticated%20label%20design%20on%20white%20background%2C%20elegant%20product%20photography%2C%20luxury%20feel&width=400&height=300&seq=prod-glacier-premium&orientation=landscape',
  'prod-glacier-mini': 'https://readdy.ai/api/search-image?query=A%20small%20compact%20mineral%20water%20bottle%20with%20minimal%20label%20design%20on%20white%20background%2C%20portable%20size%2C%20clean%20product%20photography&width=400&height=300&seq=prod-glacier-mini&orientation=landscape',
  'prod-spring-standard': 'https://readdy.ai/api/search-image?query=A%20standard%20size%20spring%20water%20bottle%20with%20nature%20inspired%20label%20on%20white%20background%2C%20clean%20product%20photography&width=400&height=300&seq=prod-spring-standard&orientation=landscape',
  'prod-spring-family': 'https://readdy.ai/api/search-image?query=A%20large%20family%20size%20water%20bottle%20container%20with%20simple%20label%20on%20white%20background%2C%20practical%20household%20product&width=400&height=300&seq=prod-spring-family&orientation=landscape',
  'prod-limited-peak': 'https://readdy.ai/api/search-image?query=A%20luxurious%20crystal%20glass%20water%20bottle%20with%20intricate%20gold%20details%20on%20white%20background%2C%20exclusive%20limited%20edition%2C%20opulent%20look&width=400&height=300&seq=prod-limited-peak&orientation=landscape',
  'prod-limited-collection': 'https://readdy.ai/api/search-image?query=An%20artistic%20handcrafted%20crystal%20water%20bottle%20with%20gold%20foil%20decorations%20on%20white%20background%2C%20collector%20edition%2C%20luxury%20feel&width=400&height=300&seq=prod-limited-collection&orientation=landscape',
};

export default function CategoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="pt-32 text-center">
        <h1 className="font-heading text-2xl text-foreground-950">未找到该产品系列</h1>
        <Link to="/categories" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer whitespace-nowrap">
          返回产品系列
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${category.name}`}
        description={`昆仑雪${category.name}：${category.subtitle}。${category.description.slice(0, 100)}`}
        keywords={`昆仑雪${category.name},${category.name}高端矿泉水,昆仑山${category.name}`}
        canonicalUrl={`https://kunlunsnow.com/categories/${category.slug}`}
      />
      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '产品系列', path: '/categories' },
                { label: category.name },
              ]}
            />
          </div>
        </section>

        <section className="pb-16 md:pb-24 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 mb-14">
              <div className="lg:w-1/2">
                <img
                  src={categoryImages[category.slug]}
                  alt={`昆仑雪${category.name}`}
                  title={`昆仑雪${category.name} - ${category.subtitle}`}
                  className="w-full h-auto rounded-2xl object-cover"
                />
              </div>
              <div className="lg:w-1/2">
                <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase font-heading">
                  SERIES
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-3 mb-3">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 font-heading mb-5">{category.subtitle}</p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {category.description}
                </p>
                <Link
                  to="/categories"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部产品系列
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-8">
              系列产品
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {category.products.map((product) => (
                <div
                  key={product.name}
                  className="bg-background-100 rounded-2xl p-6 hover:bg-background-200/70 transition-colors duration-300"
                >
                  <div className="w-full h-48 rounded-xl overflow-hidden mb-5 bg-background-200">
                    <img
                      src={productImages[product.imageSeq]}
                      alt={product.name}
                      title={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-3 leading-relaxed">
                    {product.description}
                  </p>
                  <p className="font-heading text-lg font-bold text-accent-600 mb-3">
                    {product.price}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((f) => (
                      <span
                        key={f}
                        className="text-[10px] px-2 py-1 bg-secondary-100 text-secondary-700 rounded-md font-medium whitespace-nowrap"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}